import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  Tag,
  TagScales,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  getOrgMember,
  getPersonStatistics,
} from 'src/app/PeopleDetailFlyout/redux/peopleDetailFlyout.actions'
import {
  selectPeopleMembersLoadingFromState,
  selectPeopleMembersRemoveFailureFromState,
  selectPeopleMembersRemovingFromState,
} from 'src/app/PeopleMembers/redux/peopleMembers.selectors'
import { removeUserFromOrganization } from 'src/app/RemoveUserFromOrganization/redux/removeUserFromOrganization.actions'
import {
  selectOrganizationMember,
  selectUserIdFromRoute,
} from 'src/app/RemoveUserFromOrganization/redux/removeUserFromOrganization.selectors'
import LoadingPopupContent from 'src/components/LoadingPopupContent/LoadingPopupContent'
import ModalDialogForm from 'src/components/ModalDialogForm/ModalDialogForm'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import Popup from 'src/components/Popup/Popup'
import SvgFilledAlert from 'src/components/Svgs/SvgFilledAlert'
import SvgOrganizationFilled from 'src/components/Svgs/SvgOrganizationFilled'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import {
  CONFIRM_DEACTIVATE_USER_TEXT,
  ConfirmDeactivateUserText,
} from 'src/constants'
import { AvatarSizes, OrganizationActions } from 'src/enums'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import styled, { css } from 'styled-components'

const RemoveUserFromOrganization: React.FC = (): React.ReactElement => {
  const [confirmationText, setConfirmationText] = useState<string | undefined>()
  const [removalButtonClicked, setRemovalButtonClicked] = useState(false)

  const dispatch = useDispatch()
  const history = useHistory()

  const orgMemberId = useSelector(selectUserIdFromRoute)
  const orgMember = useSelector(selectOrganizationMember)
  const loading = useSelector(selectPeopleMembersLoadingFromState)
  const removing = useSelector(selectPeopleMembersRemovingFromState)
  const removingError = useSelector(selectPeopleMembersRemoveFailureFromState)
  const { analytics } = useAnalyticsContext()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const translate = useTranslate()
  const appName = useSelector(selectAppName)

  const {
    firstName,
    lastName,
    email,
    id,
    verified,
    orgRoleDisplayTagTitle,
    hasOrgOwnerPermissions,
    statistics = {
      accountsWithRegistrarCount: 0,
      activeVcnsWithSpendCount: 0,
      inactiveVcnsWithSpendCount: 0,
      activeBudgetsWithSendCount: 0,
    },
  } = orgMember || {}

  const vcCount =
    statistics.activeVcnsWithSpendCount + statistics.inactiveVcnsWithSpendCount

  const TakeNoteDiv = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
  `

  const TakeNoteInnerText = styled.span`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    flex: 1 0 0;
  `

  const isRemoveMemberFormValid =
    confirmationText === ConfirmDeactivateUserText[locale]

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (id && orgMember?.organizationId && isRemoveMemberFormValid) {
      setRemovalButtonClicked(true)
      dispatch(removeUserFromOrganization(id, orgMember?.organizationId))
      analytics?.track(
        'remove person from org - confirm removal - button press',
        {
          screen: `/orgs/${id}/remove`,
          interaction: 'button press',
          intent: 'confirm removal',
          verified: verified,
          createdAt: moment(orgMember?.createdAt).format('YYYY-MM-DD'),
        },
      )
    }
  }

  useEffect(() => {
    if (orgMember === undefined) {
      dispatch(getOrgMember(orgMemberId))
    }
  }, [])

  useEffect(() => {
    if (orgMember !== undefined && orgMember.statistics === undefined) {
      dispatch(getPersonStatistics(orgMemberId))
    }
  }, [orgMember])

  return (
    <Popup
      css={css`
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          width: 100%;
        `)}
      `}
    >
      {loading ? (
        <LoadingPopupContent />
      ) : orgMember === undefined ? (
        <NotFoundPopupContent />
      ) : (
        <ModalDialogForm
          formOnSubmit={handleFormSubmit}
          actionButtonLabel={translate('Confirm Deactivation')}
          actionButtonLoading={removing}
          actionButtonVariant="severe"
          cancelButtonLabel={translate('Cancel')}
          cancelButtonOnClick={(): void => {
            history.goBack()
          }}
          showCancelAsLink
          isFormValid={isRemoveMemberFormValid}
          error={removalButtonClicked ? removingError : undefined}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 32px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  32,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )}
              `}
            >
              {translate('Deactivate User')}
            </span>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate(
                'Please confirm that you want to deactivate this person. Taking this action will remove any roles and permissions, but retain their data for your visibility in ${appName}.',
                { appName },
              )}
            </span>
            <span
              css={css`
                display: flex;
                flex-direction: column;
                gap: 12px;
                width: 100%;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )}
                `}
              >
                {translate('Person to be deactivated')}
              </span>
              <div
                css={css`
                  padding: 20px;
                  display: flex;
                  border: 1px solid ${styleConstants.colors.gray_medium};
                  border-radius: 10px;
                  background-color: ${styleConstants.colors.gray_light};
                `}
              >
                {orgMember && (
                  <UserAvatar
                    user={orgMember}
                    size={AvatarSizes.Medium}
                    css={css`
                      margin-right: 10px;
                    `}
                  />
                )}
                <div>
                  <h4
                    css={css`
                      margin-bottom: 1px;
                    `}
                  >
                    {firstName} {lastName}
                  </h4>
                  <p
                    css={css`
                      margin-bottom: 4px;
                      ${getFontCss(
                        12,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  >
                    {email}
                  </p>
                  <Tag
                    size={TagSizes.Small}
                    text={translate(orgRoleDisplayTagTitle || '')}
                    icon={
                      hasOrgOwnerPermissions ? (
                        <SvgOrganizationFilled
                          style={TagScales.medium}
                          css={css`
                            margin-top: -1px;
                            margin-left: -2px;
                          `}
                        />
                      ) : undefined
                    }
                  />
                </div>
              </div>
            </span>
            <span
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 12px;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )}
                `}
              >
                {translate('Take note')}
              </span>
              <span
                css={css`
                  display: flex;
                  width: 500px;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 16px;
                `}
              >
                {vcCount > 0 && (
                  <TakeNoteDiv>
                    <SvgFilledAlert
                      css={css`
                        display: flex;
                        width: fit-content;
                        height: fit-content;
                        padding: 1.5px;
                        justify-content: center;
                      `}
                    />
                    <TakeNoteInnerText key={OrganizationActions.NOTHING}>
                      <span
                        css={css`
                          ${getFontCss(
                            14,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.medium,
                          )}
                        `}
                      >
                        {translate('Virtual Cards (${vcns})', {
                          vcns: <span>{vcCount}</span>,
                        })}
                      </span>
                      <p
                        css={css`
                          ${getFontCss(
                            14,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        {translate(
                          'All virtual cards created and issued to the member will be permanently closed and cannot be reactivated',
                        )}
                      </p>
                    </TakeNoteInnerText>
                  </TakeNoteDiv>
                )}
                {statistics.activeBudgetsWithSendCount > 0 && (
                  <TakeNoteDiv>
                    <SvgFilledAlert
                      css={css`
                        display: flex;
                        width: fit-content;
                        height: fit-content;
                        padding: 1.5px;
                        justify-content: center;
                        align-items: center;
                      `}
                    />
                    <TakeNoteInnerText key={OrganizationActions.DEACTIVATE}>
                      <span
                        css={css`
                          ${getFontCss(
                            14,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.medium,
                          )}
                        `}
                      >
                        {translate('Budgets (${budgets})', {
                          budgets: (
                            <span>{statistics.activeBudgetsWithSendCount}</span>
                          ),
                        })}
                      </span>
                      <p
                        css={css`
                          ${getFontCss(
                            14,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        {translate(
                          'They will lose access to all budget(s). If they are the sole Budget User on any budget(s), then the Budget Creator will be added to the budget(s). All budgets and virtual cards created from them will remain active.',
                        )}
                      </p>
                    </TakeNoteInnerText>
                  </TakeNoteDiv>
                )}
                <TakeNoteDiv>
                  <SvgFilledAlert
                    css={css`
                      display: flex;
                      width: fit-content;
                      height: fit-content;
                      padding: 1.5px;
                      justify-content: center;
                      align-items: center;
                      margin-right: 2px;
                    `}
                  />
                  <TakeNoteInnerText key="Deactivated">
                    <p
                      css={css`
                        ${getFontCss(
                          14,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      {translate(
                        'Member profile will be deactivated. They will no longer have access to the org.',
                      )}
                    </p>
                  </TakeNoteInnerText>
                </TakeNoteDiv>
              </span>
            </span>
            <span
              css={css`
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-bottom: 40px;
                width: 100%;
              `}
            >
              <TextInputField
                allCaps
                design={TextInputFieldDesigns.New}
                name="remove-confirmation"
                fieldLabel={
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {translate('Confirm by typing ${confirmString} below:', {
                      confirmString: (
                        <span style={{ color: `${styleConstants.colors.red}` }}>
                          {translate(`"${CONFIRM_DEACTIVATE_USER_TEXT}"`)}
                        </span>
                      ),
                    })}
                  </span>
                }
                placeholder={translateSingleString(
                  locale,
                  'Please type "DEACTIVATE"',
                )}
                onChange={(value: string): void =>
                  setConfirmationText(value.toUpperCase())
                }
              />
            </span>
          </div>
        </ModalDialogForm>
      )}
    </Popup>
  )
}

export default RemoveUserFromOrganization
